import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CompanyEnums } from 'src/app/enum/company-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { FormatDetailTableModel } from 'src/app/models/tables/format/format-detail-table.model';
import { FormatTableModel } from 'src/app/models/tables/format/format-table.model';
import { FormatDetailService } from 'src/app/services/formats/format-detail.service';
import { FormatService } from 'src/app/services/formats/format.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-format',
  templateUrl: './format.component.html',
  styleUrls: ['./format.component.scss'],
  standalone: false
})
export class FormatComponent implements OnInit, OnDestroy {

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  companyId: number = 0;
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<FormatTableModel> = new PagedResponseModel<FormatTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  detailData: PagedResponseModel<FormatDetailTableModel> = new PagedResponseModel<FormatDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  limit: number = 10;
  nameWindow: string = 'Configuración de Formatos';
  page: number = 1;
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];
  url: string = "";

  constructor(private companyService: CompanyService,
    private formatService: FormatService,
    private formatDetailService: FormatDetailService,
    private headerService: HeaderService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService) { }

  async ngOnInit() {
    //Se comenta ya que no se puede crear desde esta ventana
    // this.getCompanyByUserAutheticated();
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem('format');
    if (filters) this.filters = JSON.parse(filters);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  createNewFormat() {
    if (this.companyId == CompanyEnums.altosSoftware) {
      this.router.navigate([`/format/register`]);
    } else {
      this.toastHandlerService.showToast(
        'error',
        'Solo está permitido crear formatos en la compañía principal.'
      );
    }
  }

  async exportDetailConsultation() {
    try {
      const response = await firstValueFrom(this.formatDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Formato Detalles.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar el detalle de los Formatos.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const response = await firstValueFrom(this.formatService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Formatos.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar los Formatos.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.formatDetailService.getColumnsForDetailConsultation());
      }
    } catch (error) {
      console.error('Error al obtener las columnas para consulta detalle.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.formatService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al obtener las columnas para consulta general.', error);
    }
  }

  async getCompanyByUserAutheticated() {
    try {
      this.companyId = (await firstValueFrom(this.companyService.getCompanyByUserAutheticated())).companyId;
    } catch (error) {
      console.error('Error al obtener la compañía del usuario autenticado.', error);
    }
  }

  async getFormatDetailsWithPagination() {
    try {
      this.detailData = await firstValueFrom(
        this.formatDetailService.getAllFormatDetailsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters)
      );
    } catch (error) {
      console.error('Error al buscar los detalles de los formatos.', error);
    }
  }

  async getFormatsWithPagination() {
    try {
      this.generalData = await firstValueFrom(
        this.formatService.getAllFormatsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters)
      );
    } catch (error) {
      console.error('Error al buscar los formatos.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 1:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getFormatsWithPagination()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 2:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getFormatDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  onAction(event: { action: string, item?: FormatTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/format/register/${event.item.id}`]);
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 1:
        await this.exportGeneralConsultation();
        break;
      case 2:
        await this.exportDetailConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 1:
        await this.getFormatsWithPagination();
        break;
      case 2:
        await this.getFormatDetailsWithPagination();
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}
